import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { invalidateConfigCache } from '@/lib/configCache';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useBrandingStore } from '@/stores/brandingStore';
import {
  Rocket, Building2, Users, Settings, Check, ArrowRight, ArrowLeft,
  SkipForward, Briefcase, Paintbrush, DollarSign, Package, PartyPopper,
} from 'lucide-react';

const STEPS = [
  { id: 'company', title: 'Company Profile', icon: Briefcase, desc: 'Tell us about your business' },
  { id: 'branding', title: 'Brand Your Platform', icon: Paintbrush, desc: 'Customize colors & identity' },
  { id: 'branch', title: 'Create First Branch', icon: Building2, desc: 'Set up your main location' },
  { id: 'currency', title: 'Currency & Tax', icon: DollarSign, desc: 'Configure financial settings' },
  { id: 'product', title: 'First Product', icon: Package, desc: 'Add your first stock item' },
  { id: 'employee', title: 'Add Team Member', icon: Users, desc: 'Invite your first employee' },
];

const INDUSTRY_TYPES = [
  'Retail', 'Wholesale', 'Restaurant / Cafe', 'Grocery', 'Electronics',
  'Fashion / Clothing', 'Pharmacy', 'Construction', 'Services', 'Manufacturing', 'Other',
];

const CURRENCIES = [
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SR' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '\u20AC' },
  { code: 'GBP', name: 'British Pound', symbol: '\u00A3' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'AED' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KWD' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'EGP' },
  { code: 'INR', name: 'Indian Rupee', symbol: '\u20B9' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '\u20BA' },
];

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState({});
  const [showWelcome, setShowWelcome] = useState(true);
  const [showComplete, setShowComplete] = useState(false);
  const globalUpdateBranding = useBrandingStore(s => s.updateBranding);

  // Forms
  const [companyForm, setCompanyForm] = useState({ company_name: '', address_line1: '', city: '', country: 'Saudi Arabia', phone: '', industry: '' });
  const [brandForm, setBrandForm] = useState({ app_name: '', primary_color: '#f97316', accent_color: '#d97706', login_bg_color: '#ea580c' });
  const [branchForm, setBranchForm] = useState({ name: '', location: '', phone: '' });
  const [currencyForm, setCurrencyForm] = useState({ primary_currency: 'SAR' });
  const [taxForm, setTaxForm] = useState({ name: 'VAT', rate: '15', type: 'vat', is_default: true });
  const [productForm, setProductForm] = useState({ name: '', category: '', unit: 'pcs', selling_price: '', cost_price: '', stock_quantity: '' });
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', phone: '', job_title: '', salary: '' });

  const completedCount = Object.keys(completed).length;
  const progress = Math.round((completedCount / STEPS.length) * 100);

  const handleSaveCompany = async () => {
    if (!companyForm.company_name) return toast.error('Company name is required');
    setLoading(true);
    try {
      await api.put('/settings/company', companyForm);
      invalidateConfigCache('company_settings');
      toast.success('Company profile saved!');
      setCompleted(p => ({ ...p, company: true }));
      setStep(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleSaveBranding = async () => {
    setLoading(true);
    try {
      await api.put('/branding', brandForm);
      globalUpdateBranding(brandForm);
      toast.success('Branding applied!');
      setCompleted(p => ({ ...p, branding: true }));
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleCreateBranch = async () => {
    if (!branchForm.name) return toast.error('Branch name is required');
    setLoading(true);
    try {
      await api.post('/branches', branchForm);
      toast.success('Branch created!');
      setCompleted(p => ({ ...p, branch: true }));
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create branch'); }
    finally { setLoading(false); }
  };

  const handleSaveCurrencyTax = async () => {
    setLoading(true);
    try {
      await api.put('/settings/currency-config', { primary_currency: currencyForm.primary_currency });
      await api.post('/accounting/tax-rates', { ...taxForm, rate: parseFloat(taxForm.rate) });
      toast.success('Financial settings saved!');
      setCompleted(p => ({ ...p, currency: true }));
      setStep(4);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };

  const handleCreateProduct = async () => {
    if (!productForm.name) return toast.error('Product name is required');
    setLoading(true);
    try {
      await api.post('/items', {
        ...productForm,
        selling_price: parseFloat(productForm.selling_price) || 0,
        cost_price: parseFloat(productForm.cost_price) || 0,
        stock_quantity: parseFloat(productForm.stock_quantity) || 0,
      });
      toast.success('Product added!');
      setCompleted(p => ({ ...p, product: true }));
      setStep(5);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add product'); }
    finally { setLoading(false); }
  };

  const handleCreateEmployee = async () => {
    if (!employeeForm.name) return toast.error('Employee name is required');
    setLoading(true);
    try {
      await api.post('/employees', { ...employeeForm, salary: parseFloat(employeeForm.salary) || 0 });
      toast.success('Employee added!');
      setCompleted(p => ({ ...p, employee: true }));
      setShowComplete(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add employee'); }
    finally { setLoading(false); }
  };

  const handleFinishOnboarding = async () => {
    try {
      await api.put('/tenants/onboarding', { onboarding_completed: true });
      toast.success('Setup complete! Welcome aboard');
      navigate('/');
    } catch { navigate('/'); }
  };

  const handleSkip = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      setShowComplete(true);
    }
  };

  const SUBMIT_HANDLERS = [handleSaveCompany, handleSaveBranding, handleCreateBranch, handleSaveCurrencyTax, handleCreateProduct, handleCreateEmployee];
  const SUBMIT_LABELS = ['Save Profile', 'Apply Branding', 'Create Branch', 'Save Settings', 'Add Product', 'Add Employee'];

  // Welcome screen
  if (showWelcome) {
    return (
      <DashboardLayout>
        <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="onboarding-welcome">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center mx-auto mb-8 shadow-xl shadow-primary/25">
            <Rocket className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-foreground dark:text-white" data-testid="welcome-title">
            Welcome to Smarto
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground mt-3 text-base leading-relaxed max-w-md mx-auto">
            Let's get your business set up in a few quick steps. You can always change these settings later.
          </p>
          <div className="mt-10 flex flex-col gap-3 items-center">
            <Button
              className="h-12 px-10 rounded-2xl bg-gradient-to-r from-primary to-amber-500 text-white font-semibold text-base shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all"
              onClick={() => setShowWelcome(false)}
              data-testid="start-setup-btn"
            >
              Start Setup <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button variant="ghost" className="text-muted-foreground text-sm" onClick={handleFinishOnboarding} data-testid="skip-all-btn">
              Skip for now, I'll set up later
            </Button>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-left">
            {STEPS.slice(0, 3).map(s => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="bg-muted/50 dark:bg-muted/50 rounded-xl p-4">
                  <Icon className="w-5 h-5 text-primary mb-2" />
                  <div className="text-xs font-semibold text-foreground dark:text-muted-foreground">{s.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{s.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Completion screen
  if (showComplete) {
    return (
      <DashboardLayout>
        <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="onboarding-complete">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-8 shadow-xl shadow-emerald-500/25">
            <PartyPopper className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-foreground dark:text-white" data-testid="complete-title">
            You're All Set!
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground mt-3 text-base leading-relaxed max-w-md mx-auto">
            Your business is configured and ready to go. You completed {completedCount} of {STEPS.length} setup steps.
          </p>
          <div className="mt-6 flex justify-center gap-2 flex-wrap">
            {STEPS.map(s => (
              <div key={s.id} className={`px-3 py-1.5 rounded-full text-xs font-medium ${completed[s.id] ? 'bg-success/20 text-success dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground'}`}>
                {completed[s.id] ? <Check className="w-3 h-3 inline mr-1" /> : null}{s.title}
              </div>
            ))}
          </div>
          <Button
            className="mt-10 h-12 px-10 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold text-base shadow-lg shadow-emerald-600/20"
            onClick={handleFinishOnboarding}
            data-testid="go-to-dashboard-btn"
          >
            Go to Dashboard <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-8 px-4" data-testid="onboarding-wizard">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold font-outfit tracking-tight text-foreground dark:text-white" data-testid="onboarding-title">
            Business Setup
          </h1>
          <p className="text-[13px] text-muted-foreground mt-1 font-medium">Step {step + 1} of {STEPS.length}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">{progress}% complete</span>
            <span className="text-xs text-muted-foreground">{completedCount}/{STEPS.length} steps</span>
          </div>
          <div className="h-2 bg-muted dark:bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-primary to-amber-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
          </div>
        </div>

        {/* Step Dots */}
        <div className="flex items-center justify-center gap-1.5 mb-8">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold transition-all cursor-pointer ${
                completed[s.id] ? 'bg-success text-white shadow-sm' :
                i === step ? 'bg-primary text-white ring-4 ring-primary/10' :
                'bg-muted dark:bg-muted text-muted-foreground'
              }`}
              data-testid={`step-dot-${i}`}
            >
              {completed[s.id] ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </button>
          ))}
        </div>

        {/* Step Card */}
        <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.04),0_4px_16px_rgba(0,0,0,0.06)] rounded-2xl dark:bg-foreground/50 dark:shadow-none dark:border dark:border-stone-800" data-testid={`onboarding-step-${step}`}>
          <CardHeader className="pb-4 px-6 pt-6">
            <div className="flex items-center gap-3">
              {(() => { const Icon = STEPS[step].icon; return <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>; })()}
              <div>
                <CardTitle className="text-lg font-outfit dark:text-white">{STEPS[step].title}</CardTitle>
                <p className="text-[12px] text-muted-foreground font-medium">{STEPS[step].desc}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 px-6 pb-6">
            {/* Step 0: Company */}
            {step === 0 && (
              <>
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input data-testid="company-name" placeholder="e.g. Smart Standards Co." value={companyForm.company_name} onChange={e => setCompanyForm(p => ({ ...p, company_name: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Select value={companyForm.industry} onValueChange={v => setCompanyForm(p => ({ ...p, industry: v }))}>
                      <SelectTrigger data-testid="company-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>{INDUSTRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input data-testid="company-country" value={companyForm.country} onChange={e => setCompanyForm(p => ({ ...p, country: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input data-testid="company-city" placeholder="Riyadh" value={companyForm.city} onChange={e => setCompanyForm(p => ({ ...p, city: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input data-testid="company-phone" placeholder="+966..." value={companyForm.phone} onChange={e => setCompanyForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input data-testid="company-address" placeholder="Street address" value={companyForm.address_line1} onChange={e => setCompanyForm(p => ({ ...p, address_line1: e.target.value }))} />
                </div>
              </>
            )}

            {/* Step 1: Branding */}
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input data-testid="brand-name" placeholder="e.g. MyBiz" value={brandForm.app_name} onChange={e => setBrandForm(p => ({ ...p, app_name: e.target.value }))} />
                  <p className="text-[11px] text-muted-foreground">This name appears in the sidebar and login page</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={brandForm.primary_color} onChange={e => setBrandForm(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="brand-primary-color" />
                      <Input value={brandForm.primary_color} onChange={e => setBrandForm(p => ({ ...p, primary_color: e.target.value }))} className="text-xs font-mono" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={brandForm.accent_color} onChange={e => setBrandForm(p => ({ ...p, accent_color: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="brand-accent-color" />
                      <Input value={brandForm.accent_color} onChange={e => setBrandForm(p => ({ ...p, accent_color: e.target.value }))} className="text-xs font-mono" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Login Background</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={brandForm.login_bg_color} onChange={e => setBrandForm(p => ({ ...p, login_bg_color: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="brand-login-color" />
                      <Input value={brandForm.login_bg_color} onChange={e => setBrandForm(p => ({ ...p, login_bg_color: e.target.value }))} className="text-xs font-mono" />
                    </div>
                  </div>
                </div>
                {/* Live Preview */}
                <div className="rounded-xl overflow-hidden mt-2" style={{ background: `linear-gradient(135deg, ${brandForm.login_bg_color}, ${brandForm.primary_color})` }}>
                  <div className="p-4 text-white text-center">
                    <div className="text-lg font-bold font-outfit tracking-wider">{brandForm.app_name || 'Your Brand'}</div>
                    <div className="text-xs text-white/60 mt-1">Live Preview</div>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Branch */}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label>Branch Name *</Label>
                  <Input data-testid="branch-name" placeholder="e.g. Main Branch" value={branchForm.name} onChange={e => setBranchForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input data-testid="branch-location" placeholder="City / Area" value={branchForm.location} onChange={e => setBranchForm(p => ({ ...p, location: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input data-testid="branch-phone" placeholder="+966..." value={branchForm.phone} onChange={e => setBranchForm(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Currency & Tax */}
            {step === 3 && (
              <>
                <div className="space-y-2">
                  <Label>Primary Currency</Label>
                  <Select value={currencyForm.primary_currency} onValueChange={v => setCurrencyForm(p => ({ ...p, primary_currency: v }))}>
                    <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.name} ({c.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="border-t border-border dark:border-stone-800 pt-4 mt-4">
                  <p className="text-sm font-semibold text-foreground dark:text-muted-foreground mb-3">Tax Configuration</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Tax Name</Label>
                      <Input data-testid="tax-name" value={taxForm.name} onChange={e => setTaxForm(p => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Rate (%)</Label>
                      <Input data-testid="tax-rate" type="number" value={taxForm.rate} onChange={e => setTaxForm(p => ({ ...p, rate: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={taxForm.type} onValueChange={v => setTaxForm(p => ({ ...p, type: v }))}>
                        <SelectTrigger data-testid="tax-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vat">VAT</SelectItem>
                          <SelectItem value="sales_tax">Sales Tax</SelectItem>
                          <SelectItem value="service_tax">Service Tax</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Step 4: First Product */}
            {step === 4 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Product Name *</Label>
                    <Input data-testid="product-name" placeholder="e.g. Coffee Beans 1kg" value={productForm.name} onChange={e => setProductForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input data-testid="product-category" placeholder="e.g. Beverages" value={productForm.category} onChange={e => setProductForm(p => ({ ...p, category: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Selling Price</Label>
                    <Input data-testid="product-price" type="number" placeholder="0" value={productForm.selling_price} onChange={e => setProductForm(p => ({ ...p, selling_price: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Cost Price</Label>
                    <Input data-testid="product-cost" type="number" placeholder="0" value={productForm.cost_price} onChange={e => setProductForm(p => ({ ...p, cost_price: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Stock Qty</Label>
                    <Input data-testid="product-qty" type="number" placeholder="0" value={productForm.stock_quantity} onChange={e => setProductForm(p => ({ ...p, stock_quantity: e.target.value }))} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select value={productForm.unit} onValueChange={v => setProductForm(p => ({ ...p, unit: v }))}>
                    <SelectTrigger data-testid="product-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pcs">Pieces</SelectItem>
                      <SelectItem value="kg">Kilograms</SelectItem>
                      <SelectItem value="liter">Liters</SelectItem>
                      <SelectItem value="box">Boxes</SelectItem>
                      <SelectItem value="pack">Packs</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Step 5: Employee */}
            {step === 5 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input data-testid="emp-name" placeholder="Full Name" value={employeeForm.name} onChange={e => setEmployeeForm(p => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input data-testid="emp-email" type="email" placeholder="email@company.com" value={employeeForm.email} onChange={e => setEmployeeForm(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Job Title</Label>
                    <Input data-testid="emp-title" placeholder="e.g. Manager" value={employeeForm.job_title} onChange={e => setEmployeeForm(p => ({ ...p, job_title: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Salary</Label>
                    <Input data-testid="emp-salary" type="number" placeholder="0" value={employeeForm.salary} onChange={e => setEmployeeForm(p => ({ ...p, salary: e.target.value }))} />
                  </div>
                </div>
              </>
            )}

            {/* Submit Button */}
            <Button
              className="w-full bg-primary hover:bg-primary/90 rounded-xl h-10 mt-2"
              disabled={loading}
              onClick={SUBMIT_HANDLERS[step]}
              data-testid="step-submit-btn"
            >
              {loading ? 'Saving...' : SUBMIT_LABELS[step]}
            </Button>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button variant="ghost" size="sm" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="onboarding-back" className="text-[13px] text-muted-foreground dark:text-muted-foreground">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleSkip} data-testid="onboarding-skip" className="text-[13px] text-muted-foreground">
              <SkipForward className="w-4 h-4 mr-1" /> Skip
            </Button>
            {completedCount >= 3 && (
              <Button className="bg-success hover:bg-emerald-700 rounded-xl shadow-sm shadow-emerald-600/20" onClick={() => setShowComplete(true)} data-testid="finish-onboarding-btn">
                Finish Setup <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
